import { Injectable, inject } from '@angular/core';
import {
  Auth,
} from '@angular/fire/auth';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { deleteField, serverTimestamp } from '@angular/fire/firestore';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';

import { ToastrService } from 'ngx-toastr';
import {
  Observable,
  Subject,
  catchError,
  exhaustMap,
  from,
  map,
  of,
  switchMap,
  take,
} from 'rxjs';
import { AppExceptionModel } from '../../models/app-exception.model';
import { FireTimestamp } from '../../models/timestamp.firestore.model';
import { UserModel } from '../../models/user.model';
import { AuthService } from '../../services/auth.service';
import { UserService } from '../../services/user.service';
import * as userActions from '../actions/user.actions';

@Injectable()
export class UserEffects {
  actions$ = inject(Actions);
  authService = inject(AuthService);
  userService = inject(UserService);
  afs = inject(AngularFirestore);
  afAuth = inject(Auth);
  router = inject(Router);
  toastr = inject(ToastrService);

  private logout$ = new Subject<void>();

  emailLogin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.emailLogin),
      exhaustMap(({ username, password }) =>
        from(this.authService.loginWithPassword(username, password)).pipe(
          map((user) => {
            if (!user) throw new Error('Invalid username or password');
            if (!user.emailVerified) {
              throw new Error(
                'Email verification pending. Please check your mailbox',
              );
            }
            return userActions.loginSuccess({
              user: {
                uid: user.uid,
                email: user.email,
                name: user.displayName,
                photoURL: user.photoURL,
              } as UserModel,
            });
          }),
          catchError((err) => {
            return of(
              userActions.loginFailure({
                error: err?.message ?? 'Login failed',
              }),
            );
          }),
        ),
      ),
    ),
  );



  authenticatedToStore$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.authenticated),
      switchMap(({ user }) => {
        return of(userActions.loginSuccess({ user }));
      }),
    ),
  );

  setClarityUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.loadUserSuccess),
      map(({ user }) => {
        // console.debug('[Clarity] Setting user', user);

        try {
          const clarity = (window as any).clarity;

          // Ensure Clarity script is loaded
          if (clarity && typeof clarity === 'function') {
            clarity('identify', user.uid);

            // Optionally set custom metadata
            clarity('set', 'user_email', user.email);
            clarity('set', 'user_name', user.name);
          } else {
            // console.warn('[Clarity] Not loaded yet, retrying in 1s...');
            // Retry once after a short delay (simple fallback)
            setTimeout(() => {
              (window as any).clarity?.('identify', user.uid);
              (window as any).clarity?.('set', 'user_email', user.email);
              (window as any).clarity?.('set', 'user_name', user.name);
            }, 1000);
          }
        } catch (e) {
          console.error('[Clarity] Error setting user', e);
        }

        return { type: '[Clarity] Set User Success' };
      }),
    ),
  );

  loadUser$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.authenticated),
      switchMap(({ user }) => {
        this.logout$.next();
        // console.log(`loadUser$: Authenticated User Uid: ${user.uid}`);
        return this.afs.collection<UserModel>('users').doc(user.uid).get();
      }),
      switchMap((snapshot) => {
        // console.log(`loadUser$: User document Exists: ${snapshot.exists}`);
        if (!snapshot.exists)
          return of(userActions.loadUserFailure({ error: 'User not found' }));
        let u: any = snapshot.data();
        u.uid = snapshot.id;
        if (u.dob != null) {
          u.dob = new FireTimestamp(
            u.dob?.seconds,
            u.dob?.nanoseconds,
          ).toDate();
        }
        return of(userActions.loadUserSuccess({ user: u }));
      }),
      catchError((error) => {
        this.toastr.error('Unable to load user profile', 'Error loading user');
        return of(userActions.loadUserFailure({ error: error }));
      }),
    ),
  );

  getUser$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.getUser),
      switchMap(({ uid }) => {
        this.logout$.next();
        const docRef = this.afs.doc<UserModel>(`users/${uid}`);
        return docRef.get().pipe(
          take(1),
          map((snapshot) => {
            // console.log(`loadUser$: User document get complete`);
            if (!snapshot.exists) return null;
            let u: any = snapshot.data();
            u.uid = uid;
            if (u.dob != null) {
              u.dob = new FireTimestamp(
                u.dob?.seconds,
                u.dob?.nanoseconds,
              ).toDate();
            }
            return u;
          }),
        );
      }),
      map((u) => {
        if (u != null) {
          return userActions.loadUserSuccess({ user: u });
        } else {
          this.toastr.error(
            'User not found. Please try again later',
            'Error loading user profile',
          );
          return userActions.loadUserFailure({ error: 'User not found' });
        }
      }),
    ),
  );

  updateUser$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.update),
      switchMap(async ({ user }) => {
        const docRef = this.afs.collection('users').doc(user.uid);
        let temp: any = { ...user, modified: serverTimestamp() };
        delete temp.uid;

        // console.log('Updating user profile: ', JSON.stringify(temp, null, 2));

        try {
          await docRef.set(temp, { merge: true });
          this.toastr.success(
            'User profile updated successfully',
            'Update Successful',
          );
          return userActions.getUser({ uid: user.uid! });
        } catch (error) {
          console.error(`Error: ${JSON.stringify(error, null, 2)}`);
          this.toastr.error(
            `Error updating user profile. ${error}`,
            'Update failed',
          );
          return userActions.updateFailure({ error });
        }
      }),
    ),
  );

  deleteImage$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.deleteImage),
      switchMap(async ({ user }) => {
        const docRef = this.afs.collection('users').doc(user.uid);

        let temp = {
          ...user,
          photoURL: deleteField(),
          storagePath: deleteField(),
        };
        delete temp.uid;

        try {
          return await docRef.update(temp).then(() => {
            return userActions.updateSuccess({
              user: { ...user, photoURL: undefined, storagePath: undefined },
            });
          });
        } catch (error) {
          this.toastr.error(
            `Error updating user profile.\n${error}`,
            'Update failed',
          );
          // console.log('Error updating user profile', error);
          return userActions.updateFailure({ error });
        }
      }),
    ),
  );

  signUp$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.signup),
      switchMap(async ({ email, password }) => {
        try {
          await this.authService.signUp(email, password);
          return userActions.signupSuccess();
        } catch (error) {
          this.toastr.error(
            (error as AppExceptionModel).message,
            'Signup failed',
          );
          return userActions.signupFailure({ error });
        }
      }),
    ),
  );

  signupSuccess$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.signupSuccess),
      switchMap(async () => {
        this.toastr.success(
          'Account created. Kindly check your registered email for account verification e-mail.',
          'Signup successful',
        );
        return userActions.signupSuccess();
      }),
    ),
  );

  resetPassword$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.resetPassword),
      switchMap(async ({ email }) => {
        try {
          await this.authService.sendPasswordResetEmail(email);
          return userActions.resetPasswordSuccess();
        } catch (error) {
          const errorMessage =
            (error as AppExceptionModel)?.message ??
            (error as any)?.message ??
            'Failed to send password reset email';
          return userActions.resetPasswordFailure({ error: errorMessage });
        }
      }),
    ),
  );

  userLogout$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.logout),
      switchMap(() => this.authService.logout()),
      map(() => {
        this.router.navigate(['auth/login']);
        this.logout$.next();
        return userActions.logoutSuccess();
      }),
    ),
  );

  constructor() {}
}
